import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit, FiTrash, FiAlertTriangle, FiPackage, FiTrendingUp, FiTrendingDown, FiDownload, FiEye, FiCalendar } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const StockManagement = () => {
  const [inventory, setInventory] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [showTransactionsModal, setShowTransactionsModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown && !event.target.closest('.dropdown-container')) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [activeDropdown]);
  const [showLowStock, setShowLowStock] = useState(false);
  const [activeTab, setActiveTab] = useState('items'); // 'items' or 'transactions'

  const [inventoryForm, setInventoryForm] = useState({
    itemName: '',
    category: 'vegetables',
    quantity: '',
    unit: 'kg',
    costPerUnit: '',
    minQuantity: '',
    lastRestocked: new Date().toISOString().split('T')[0]
  });

  const [receiptForm, setReceiptForm] = useState({
    inventoryId: '',
    quantity: '',
    costPerUnit: '',
    notes: '',
    transactionDate: new Date().toISOString().split('T')[0]
  });

  const [issueForm, setIssueForm] = useState({
    inventoryId: '',
    quantity: '',
    notes: '',
    transactionDate: new Date().toISOString().split('T')[0]
  });

  const [filterDates, setFilterDates] = useState({
    startDate: '',
    endDate: ''
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [transactionPage, setTransactionPage] = useState(1);
  const [itemTransactions, setItemTransactions] = useState([]);
  const [itemTransactionPage, setItemTransactionPage] = useState(1);
  const [itemTransactionsPerPage, setItemTransactionsPerPage] = useState(10);
  const [transactionTypeFilter, setTransactionTypeFilter] = useState('');
  const [itemFilter, setItemFilter] = useState('');
  const [inventoryPerPage, setInventoryPerPage] = useState(10);
  const [transactionsPerPageValue, setTransactionsPerPageValue] = useState(15);

  const categories = ['vegetables', 'fruits', 'meat', 'dairy', 'spices', 'grains', 'beverages', 'other'];
  const units = ['kg', 'g', 'l', 'ml', 'pcs', 'dozen', 'packet', 'other'];

  useEffect(() => {
    fetchInventory();
    fetchAllTransactions();
  }, [showLowStock]);

  const fetchInventory = async () => {
    setLoading(true);
    try {
      const params = showLowStock ? '?lowStock=true' : '';
      const response = await api.get(`/vendor/inventory${params}`);
      setInventory(response.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch inventory');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllTransactions = async () => {
    try {
      const params = new URLSearchParams();
      if (filterDates.startDate) params.append('startDate', filterDates.startDate);
      if (filterDates.endDate) params.append('endDate', filterDates.endDate);
      if (transactionTypeFilter) params.append('transactionType', transactionTypeFilter);
      if (itemFilter) params.append('inventoryId', itemFilter);

      const response = await api.get(`/vendor/inventory/transactions?${params.toString()}`);
      setTransactions(response.data.data || []);
      setTransactionPage(1);
    } catch (error) {
      toast.error('Failed to fetch transactions');
    }
  };

  const fetchItemTransactions = async (itemId) => {
    try {
      // Get last 7 days data
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 7);

      const params = new URLSearchParams();
      params.append('startDate', startDate.toISOString().split('T')[0]);
      params.append('endDate', endDate.toISOString().split('T')[0]);

      const response = await api.get(`/vendor/inventory/${itemId}/transactions?${params.toString()}`);
      setItemTransactions(response.data.data || []);
      setItemTransactionPage(1);
      setShowTransactionsModal(true);
    } catch (error) {
      toast.error('Failed to fetch item transactions');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedItem) {
        await api.put(`/vendor/inventory/${selectedItem._id}`, inventoryForm);
        toast.success('Item updated successfully');
      } else {
        await api.post('/vendor/inventory', inventoryForm);
        toast.success('Item added successfully');
      }
      setShowModal(false);
      resetForm();
      fetchInventory();
    } catch (error) {
      toast.error('Failed to save item');
    }
  };

  const handleReceiptSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/vendor/inventory/transactions/receipt', receiptForm);
      toast.success('Stock receipt added successfully');
      setShowReceiptModal(false);
      resetReceiptForm();
      fetchInventory();
      fetchAllTransactions();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add receipt');
    }
  };

  const handleIssueSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/vendor/inventory/transactions/issue', issueForm);
      toast.success('Stock issued successfully');
      setShowIssueModal(false);
      resetIssueForm();
      fetchInventory();
      fetchAllTransactions();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to issue stock');
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await api.delete(`/vendor/inventory/${itemId}`);
      toast.success('Item deleted successfully');
      fetchInventory();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const handleDownloadReport = async () => {
    try {
      const params = new URLSearchParams();
      if (filterDates.startDate) params.append('startDate', filterDates.startDate);
      if (filterDates.endDate) params.append('endDate', filterDates.endDate);
      params.append('format', 'csv');

      const response = await api.get(`/vendor/inventory/reports/generate?${params.toString()}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `inventory-report-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Report downloaded successfully');
    } catch (error) {
      toast.error('Failed to download report');
    }
  };

  const openModal = (item = null) => {
    if (item) {
      setSelectedItem(item);
      setInventoryForm({
        itemName: item.itemName,
        category: item.category,
        quantity: item.quantity,
        unit: item.unit,
        costPerUnit: item.costPerUnit,
        minQuantity: item.minQuantity,
        lastRestocked: item.lastRestocked?.split('T')[0] || ''
      });
    }
    setShowModal(true);
  };

  const openReceiptModal = (item = null) => {
    if (item) {
      setReceiptForm({
        ...receiptForm,
        inventoryId: item._id,
        costPerUnit: item.costPerUnit || ''
      });
      setSelectedItem(item);
    }
    setShowReceiptModal(true);
  };

  const openIssueModal = (item = null) => {
    if (item) {
      setIssueForm({
        ...issueForm,
        inventoryId: item._id
      });
      setSelectedItem(item);
    }
    setShowIssueModal(true);
  };

  const resetForm = () => {
    setInventoryForm({
      itemName: '',
      category: 'vegetables',
      quantity: '',
      unit: 'kg',
      costPerUnit: '',
      minQuantity: '',
      lastRestocked: new Date().toISOString().split('T')[0]
    });
    setSelectedItem(null);
  };

  const resetReceiptForm = () => {
    setReceiptForm({
      inventoryId: '',
      quantity: '',
      costPerUnit: '',
      notes: '',
      transactionDate: new Date().toISOString().split('T')[0]
    });
    setSelectedItem(null);
  };

  const resetIssueForm = () => {
    setIssueForm({
      inventoryId: '',
      quantity: '',
      notes: '',
      transactionDate: new Date().toISOString().split('T')[0]
    });
    setSelectedItem(null);
  };

  const isLowStock = (item) => item.quantity <= item.minQuantity;

  // Pagination logic for inventory items
  const indexOfLastItem = currentPage * inventoryPerPage;
  const indexOfFirstItem = indexOfLastItem - inventoryPerPage;
  const currentItems = inventory.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(inventory.length / inventoryPerPage);

  // Pagination logic for transactions
  const indexOfLastTransaction = transactionPage * transactionsPerPageValue;
  const indexOfFirstTransaction = indexOfLastTransaction - transactionsPerPageValue;
  const currentTransactions = transactions.slice(indexOfFirstTransaction, indexOfLastTransaction);
  const totalTransactionPages = Math.ceil(transactions.length / transactionsPerPageValue);

  // Pagination logic for item transactions modal
  const indexOfLastItemTransaction = itemTransactionPage * itemTransactionsPerPage;
  const indexOfFirstItemTransaction = indexOfLastItemTransaction - itemTransactionsPerPage;
  const currentItemTransactions = itemTransactions.slice(indexOfFirstItemTransaction, indexOfLastItemTransaction);
  const totalItemTransactionPages = Math.ceil(itemTransactions.length / itemTransactionsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const paginateTransactions = (pageNumber) => setTransactionPage(pageNumber);
  const paginateItemTransactions = (pageNumber) => setItemTransactionPage(pageNumber);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50 p-3 sm:p-4 md:p-6">
      {/* Page Header */}
      <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">Stock Management</h1>
            <p className="text-gray-600 text-sm sm:text-base">Track stock items, receipts, and issues</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 items-stretch sm:items-center">

            {/* Download Button */}
            <button
              className="bg-green-500 hover:bg-green-600 text-white px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 text-sm sm:text-base w-full sm:w-auto"
              onClick={handleDownloadReport}
            >
              <FiDownload className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Download Report</span>
              <span className="sm:hidden">Report</span>
            </button>

            {/* Add Button */}
            <button
              className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-3 sm:px-5 py-2 sm:py-2.5 rounded-xl font-semibold shadow-md transition-all duration-200 hover:scale-105 flex items-center justify-center gap-2 text-sm sm:text-base w-full sm:w-auto"
              onClick={() => openModal()}
            >
              <FiPlus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Add Item</span>
              <span className="sm:hidden">Add</span>
            </button>

          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl shadow-lg p-2 mb-6">
        <div className="flex space-x-1">
          <button
            className={`flex-1 sm:flex-none px-3 sm:px-6 py-2 sm:py-3 rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-2 text-sm sm:text-base ${activeTab === 'items'
              ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg'
              : 'text-gray-600 hover:bg-gray-100'
              }`}
            onClick={() => setActiveTab('items')}
          >
            <FiPackage size={16} className="sm:size-18" />
            <span className="hidden sm:inline">Inventory Items</span>
            <span className="sm:hidden">Items</span>
          </button>
          <button
            className={`flex-1 sm:flex-none px-3 sm:px-6 py-2 sm:py-3 rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-2 text-sm sm:text-base ${activeTab === 'transactions'
              ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg'
              : 'text-gray-600 hover:bg-gray-100'
              }`}
            onClick={() => setActiveTab('transactions')}
          >
            <FiCalendar size={16} className="sm:size-18" />
            <span className="hidden sm:inline">Transaction History</span>
            <span className="sm:hidden">History</span>
          </button>
        </div>
      </div>

      {/* Items Tab */}
      {activeTab === 'items' && (
        <>
          <div className="bg-white rounded-2xl shadow-lg p-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-4 items-center">
              <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors duration-200">
                <input
                  type="checkbox"
                  checked={showLowStock}
                  onChange={(e) => setShowLowStock(e.target.checked)}
                  className="w-4 h-4 text-green-600 focus:ring-green-500 rounded cursor-pointer"
                />
                <span className="text-sm font-medium text-gray-700">Show Low Stock Only</span>
              </label>
            </div>
          </div>

          {/* Inventory Table */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-blue-50 to-blue-100 border-b border-gray-200 sticky top-0 z-10">
                  <tr>
                    <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Item Name</th>
                    <th className="hidden sm:table-cell px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Category</th>
                    <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Quantity</th>
                    <th className="hidden md:table-cell px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Unit</th>
                    <th className="hidden sm:table-cell px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Cost/Unit</th>
                    <th className="hidden lg:table-cell px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Total Value</th>
                    <th className="hidden md:table-cell px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Min Qty</th>
                    <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Status</th>
                    <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {currentItems.map((item) => (
                    <tr key={item._id} className={`transition-colors duration-150 ${isLowStock(item) ? 'bg-orange-50' : 'hover:bg-gray-50'}`}>
                      <td className="px-3 sm:px-6 py-3 sm:py-4">
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-medium text-gray-900">{item.itemName}</div>
                          {isLowStock(item) && (
                            <FiAlertTriangle className="text-orange-500 w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          )}
                        </div>
                        {/* Mobile-only additional info */}
                        <div className="sm:hidden mt-1 space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                              {item.category.charAt(0).toUpperCase() + item.category.slice(1)}
                            </span>
                            <span className="text-xs text-gray-600">{item.unit}</span>
                          </div>
                          <div className="text-xs text-gray-600">
                            ₹{item.costPerUnit} | Min: {item.minQuantity}
                          </div>
                        </div>
                      </td>
                      <td className="hidden sm:table-cell px-6 py-4">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                          {item.category.charAt(0).toUpperCase() + item.category.slice(1)}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-sm text-gray-700">{item.quantity}</td>
                      <td className="hidden md:table-cell px-6 py-4 text-sm text-gray-700">{item.unit}</td>
                      <td className="hidden sm:table-cell px-6 py-4 text-sm text-gray-700">₹{item.costPerUnit}</td>
                      <td className="hidden lg:table-cell px-6 py-4">
                        <div className="text-sm font-bold text-gray-900">₹{(item.quantity * item.costPerUnit).toFixed(2)}</div>
                      </td>
                      <td className="hidden md:table-cell px-6 py-4 text-sm text-gray-700">{item.minQuantity}</td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${isLowStock(item)
                          ? 'bg-orange-100 text-orange-800'
                          : 'bg-green-100 text-green-800'
                          }`}>
                          {isLowStock(item) ? 'Low Stock' : 'In Stock'}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4">
                        <div className="flex flex-col sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                          <button
                            className="flex items-center justify-center px-2 sm:px-3 py-1.5 sm:py-2 bg-green-100 hover:bg-green-200 text-green-700 font-semibold rounded-lg transition text-xs sm:text-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              openReceiptModal(item);
                              setActiveDropdown(null);
                            }}
                          >
                            <span className="hidden sm:inline">Add Stock</span>
                            <span className="sm:hidden">+Stock</span>
                          </button>

                          <button
                            className="flex items-center justify-center px-2 sm:px-3 py-1.5 sm:py-2 bg-orange-100 hover:bg-orange-200 text-orange-700 font-semibold rounded-lg transition text-xs sm:text-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              openIssueModal(item);
                              setActiveDropdown(null);
                            }}
                          >
                            <span className="hidden sm:inline">Issue Stock</span>
                            <span className="sm:hidden">-Stock</span>
                          </button>

                          <button
                            className="flex items-center justify-center px-2 sm:px-3 py-1.5 sm:py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 font-semibold rounded-lg transition text-xs sm:text-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              fetchItemTransactions(item._id);
                              setActiveDropdown(null);
                            }}
                          >
                            <span className="hidden sm:inline">History</span>
                            <span className="sm:hidden">📊</span>
                          </button>

                          <button
                            className="flex items-center justify-center px-2 sm:px-3 py-1.5 sm:py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-lg transition text-xs sm:text-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              openModal(item);
                              setActiveDropdown(null);
                            }}
                          >
                            <span className="hidden sm:inline">Edit</span>
                            <span className="sm:hidden">✏️</span>
                          </button>

                          <button
                            className="flex items-center justify-center px-2 sm:px-3 py-1.5 sm:py-2 bg-red-100 hover:bg-red-200 text-red-700 font-semibold rounded-lg transition text-xs sm:text-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(item._id);
                              setActiveDropdown(null);
                            }}
                          >
                            <span className="hidden sm:inline">Delete</span>
                            <span className="sm:hidden">🗑️</span>
                          </button>

                        </div>
                      </td>


                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination for Inventory Items */}
            {inventory.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-4 mt-4">
                <div className="flex flex-col gap-3 sm:gap-4">
                  <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
                    Showing {Math.min((currentPage - 1) * inventoryPerPage + 1, inventory.length)}-{Math.min(currentPage * inventoryPerPage, inventory.length)} of {inventory.length} items
                  </div>
                  <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
                    <div className="flex gap-2 items-center justify-center">
                      <button
                        className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 text-gray-700 rounded-lg font-medium transition-colors duration-200 text-xs sm:text-sm"
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <span className="hidden sm:inline">Previous</span>
                        <span className="sm:hidden">‹</span>
                      </button>
                      {[...Array(Math.ceil(inventory.length / inventoryPerPage))].map((_, idx) => {
                        const pageNum = idx + 1;
                        const totalInventoryPages = Math.ceil(inventory.length / inventoryPerPage);
                        if (
                          pageNum === 1 ||
                          pageNum === totalInventoryPages ||
                          (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                        ) {
                          return (
                            <button
                              key={pageNum}
                              className={`min-w-[28px] sm:min-w-[36px] px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg font-medium transition-all duration-200 text-xs sm:text-sm ${pageNum === currentPage
                                  ? 'bg-blue-500 text-white shadow-lg'
                                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                }`}
                              onClick={() => paginate(pageNum)}
                            >
                              {pageNum}
                            </button>
                          );
                        } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                          return <span key={pageNum} className="px-1 sm:px-2 text-gray-400 text-xs sm:text-sm">...</span>;
                        }
                        return null;
                      })}
                      <button
                        className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 text-gray-700 rounded-lg font-medium transition-colors duration-200 text-xs sm:text-sm"
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        <span className="hidden sm:inline">Next</span>
                        <span className="sm:hidden">›</span>
                      </button>
                    </div>
                    <div className="flex items-center gap-2 text-xs sm:text-sm justify-center">
                      <span className="text-gray-600">Show</span>
                      <select
                        className="px-2 sm:px-3 py-1 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200 text-xs sm:text-sm"
                        value={inventoryPerPage}
                        onChange={(e) => {
                          setInventoryPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <span className="text-gray-600">per page</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {inventory.length === 0 && (
              <div className="bg-gray-50 rounded-lg p-6 sm:p-12 text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiPackage className="text-gray-400 text-2xl sm:text-3xl" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">{showLowStock ? 'No Low Stock Items' : 'No Inventory Items Found'}</h3>
                <p className="text-gray-600 text-sm sm:text-base mb-6">{showLowStock ? 'No items are currently low in stock.' : 'Start by adding your first inventory item.'}</p>
                {!showLowStock && (
                  <button className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold shadow-lg transform transition-all duration-200 hover:scale-105 flex items-center justify-center mx-auto gap-2 text-sm sm:text-base" onClick={() => openModal()}>
                    <FiPlus className="text-sm sm:text-base" />
                    <span className="hidden sm:inline">Add Your First Item</span>
                    <span className="sm:hidden">Add Item</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Transactions Tab */}
      {activeTab === 'transactions' && (
        <>
          <div className="bg-white rounded-2xl shadow-lg p-3 sm:p-4 mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">Start Date</label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200 text-sm"
                  value={filterDates.startDate}
                  onChange={(e) => setFilterDates({ ...filterDates, startDate: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">End Date</label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200 text-sm"
                  value={filterDates.endDate}
                  onChange={(e) => setFilterDates({ ...filterDates, endDate: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">Transaction Type</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200 text-sm"
                  value={transactionTypeFilter}
                  onChange={(e) => setTransactionTypeFilter(e.target.value)}
                >
                  <option value="">All Types</option>
                  <option value="receipt">Receipt</option>
                  <option value="issue">Issue</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">Filter by Item</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200 text-sm"
                  value={itemFilter}
                  onChange={(e) => setItemFilter(e.target.value)}
                >
                  <option value="">All Items</option>
                  {inventory.map(item => (
                    <option key={item._id} value={item._id}>{item.itemName}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <button
                  className="w-full px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors duration-200 text-sm"
                  onClick={fetchAllTransactions}
                >
                  Apply Filters
                </button>
              </div>
              <div className="flex items-end">
                <button
                  className="w-full px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors duration-200 text-sm"
                  onClick={() => {
                    setFilterDates({ startDate: '', endDate: '' });
                    setTransactionTypeFilter('');
                    setItemFilter('');
                    fetchAllTransactions();
                  }}
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-blue-50 to-blue-100 border-b border-gray-200 sticky top-0 z-10">
                  <tr>
                    <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Date</th>
                    <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Item Name</th>
                    <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Type</th>
                    <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Quantity</th>
                    <th className="hidden sm:table-cell px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Cost/Unit</th>
                    <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Total Cost</th>
                    <th className="hidden md:table-cell px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {currentTransactions.map((txn) => (
                    <tr key={txn._id} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-sm text-gray-700">{new Date(txn.transactionDate).toLocaleDateString()}</td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4">
                        <div className="text-sm font-medium text-gray-900">{txn.inventoryId?.itemName}</div>
                        {/* Mobile-only additional info */}
                        <div className="sm:hidden mt-1 space-y-1">
                          <div className="text-xs text-gray-600">
                            {txn.quantity} {txn.unit} | ₹{txn.costPerUnit}
                          </div>
                          {txn.notes && (
                            <div className="text-xs text-gray-600 truncate max-w-[150px]">{txn.notes}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4">
                        {txn.transactionType === 'receipt' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                            <FiTrendingUp size={12} />
                            <span className="hidden sm:inline">Receipt</span>
                            <span className="sm:hidden">+</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium">
                            <FiTrendingDown size={12} />
                            <span className="hidden sm:inline">Issue</span>
                            <span className="sm:hidden">-</span>
                          </span>
                        )}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-sm text-gray-700">{txn.quantity} {txn.unit}</td>
                      <td className="hidden sm:table-cell px-6 py-4">
                        {txn.priceChange !== 0 ? (
                          <div className="flex items-center gap-2">
                            <span
                              style={{
                                color: txn.priceChange > 0 ? '#ef4444' : '#22c55e',
                                fontWeight: '600'
                              }}
                            >
                              ₹{txn.costPerUnit}
                            </span>
                            {txn.priceChange > 0 ? (
                              <span style={{ color: '#ef4444', fontSize: '12px' }}>
                                ↑ +₹{Math.abs(txn.priceChange)}
                              </span>
                            ) : (
                              <span style={{ color: '#22c55e', fontSize: '12px' }}>
                                ↓ -₹{Math.abs(txn.priceChange)}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: '#666' }}>₹{txn.costPerUnit}</span>
                        )}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4">
                        <div className="text-sm font-bold text-gray-900">₹{txn.totalCost?.toFixed(2)}</div>
                      </td>
                      <td className="hidden md:table-cell px-6 py-4 text-sm text-gray-700">{txn.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination for Transactions */}
            {transactions.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-4 mt-4">
                <div className="flex flex-col gap-3 sm:gap-4">
                  <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
                    Showing {Math.min((transactionPage - 1) * transactionsPerPageValue + 1, transactions.length)}-{Math.min(transactionPage * transactionsPerPageValue, transactions.length)} of {transactions.length} transactions
                  </div>
                  <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
                    <div className="flex gap-2 items-center justify-center">
                      <button
                        className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 text-gray-700 rounded-lg font-medium transition-colors duration-200 text-xs sm:text-sm"
                        onClick={() => paginateTransactions(transactionPage - 1)}
                        disabled={transactionPage === 1}
                      >
                        <span className="hidden sm:inline">Previous</span>
                        <span className="sm:hidden">‹</span>
                      </button>
                      {[...Array(Math.ceil(transactions.length / transactionsPerPageValue))].map((_, idx) => {
                        const pageNum = idx + 1;
                        const totalTxnPages = Math.ceil(transactions.length / transactionsPerPageValue);
                        if (
                          pageNum === 1 ||
                          pageNum === totalTxnPages ||
                          (pageNum >= transactionPage - 1 && pageNum <= transactionPage + 1)
                        ) {
                          return (
                            <button
                              key={pageNum}
                              className={`min-w-[28px] sm:min-w-[36px] px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg font-medium transition-all duration-200 text-xs sm:text-sm ${pageNum === transactionPage
                                  ? 'bg-blue-500 text-white shadow-lg'
                                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                }`}
                              onClick={() => paginateTransactions(pageNum)}
                            >
                              {pageNum}
                            </button>
                          );
                        } else if (pageNum === transactionPage - 2 || pageNum === transactionPage + 2) {
                          return <span key={pageNum} className="px-1 sm:px-2 text-gray-400 text-xs sm:text-sm">...</span>;
                        }
                        return null;
                      })}
                      <button
                        className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 text-gray-700 rounded-lg font-medium transition-colors duration-200 text-xs sm:text-sm"
                        onClick={() => paginateTransactions(transactionPage + 1)}
                        disabled={transactionPage === totalTransactionPages}
                      >
                        <span className="hidden sm:inline">Next</span>
                        <span className="sm:hidden">›</span>
                      </button>
                    </div>
                    <div className="flex items-center gap-2 text-xs sm:text-sm justify-center">
                      <span className="text-gray-600">Show</span>
                      <select
                        className="px-2 sm:px-3 py-1 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200 text-xs sm:text-sm"
                        value={transactionsPerPageValue}
                        onChange={(e) => {
                          setTransactionsPerPageValue(Number(e.target.value));
                          setTransactionPage(1);
                        }}
                      >
                        <option value={10}>10</option>
                        <option value={15}>15</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <span className="text-gray-600">per page</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {transactions.length === 0 && (
              <div className="bg-gray-50 rounded-lg p-6 sm:p-12 text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiCalendar className="text-gray-400 text-2xl sm:text-3xl" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">No Transactions Found</h3>
                <p className="text-gray-600 text-sm sm:text-base">No transactions found for the selected filters.</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Add/Edit Item Modal */}
      {showModal && (
       <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4">
  <div
    className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden"
    onClick={(e) => e.stopPropagation()}
  >

    {/* HEADER */}
    <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 sm:p-6 sticky top-0 z-10">
      <div className="flex justify-between items-center">
        <h2 className="text-lg sm:text-xl font-bold">
          {selectedItem ? 'Edit Item' : 'Add Item'}
        </h2>
        <button
          className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors duration-200"
          onClick={() => setShowModal(false)}
        >
          <span className="text-xl sm:text-2xl">×</span>
        </button>
      </div>
    </div>

    <form onSubmit={handleSubmit} className="flex flex-col flex-1">

      {/* SCROLLABLE CONTENT */}
      <div className="p-4 sm:p-6 overflow-y-auto flex-1">

        {/* Row 1 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              Item Name *
            </label>
            <input
              type="text"
              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
              value={inventoryForm.itemName}
              onChange={(e) =>
                setInventoryForm({ ...inventoryForm, itemName: e.target.value })
              }
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              Category *
            </label>
            <select
              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
              value={inventoryForm.category}
              onChange={(e) =>
                setInventoryForm({ ...inventoryForm, category: e.target.value })
              }
              required
            >
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Row 2 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              Quantity *
            </label>
            <input
              type="text"
              inputMode="decimal"
              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
              value={inventoryForm.quantity}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                  setInventoryForm({ ...inventoryForm, quantity: value });
                }
              }}
              required
              disabled={!!selectedItem}
              style={
                selectedItem
                  ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' }
                  : {}
              }
            />
            {selectedItem && (
              <small className="text-gray-500 text-xs">
                Use "Add Stock" or "Issue Stock" to change quantity
              </small>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              Unit *
            </label>
            <select
              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
              value={inventoryForm.unit}
              onChange={(e) =>
                setInventoryForm({ ...inventoryForm, unit: e.target.value })
              }
              required
            >
              {units.map((unit) => (
                <option key={unit} value={unit}>
                  {unit}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              Cost per Unit (₹) *
            </label>
            <input
              type="text"
              inputMode="decimal"
              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
              value={inventoryForm.costPerUnit}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                  setInventoryForm({
                    ...inventoryForm,
                    costPerUnit: value,
                  });
                }
              }}
              required
            />
          </div>
        </div>

        {/* Row 3 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              Min Stock Threshold *
            </label>
            <input
              type="text"
              inputMode="decimal"
              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
              value={inventoryForm.minQuantity}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                  setInventoryForm({
                    ...inventoryForm,
                    minQuantity: value,
                  });
                }
              }}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              Last Restocked
            </label>
            <input
              type="date"
              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
              value={inventoryForm.lastRestocked}
              onChange={(e) =>
                setInventoryForm({
                  ...inventoryForm,
                  lastRestocked: e.target.value,
                })
              }
            />
          </div>
        </div>

        {/* Total */}
        {inventoryForm.quantity && inventoryForm.costPerUnit && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">
                Total Value:
              </span>
              <span className="text-lg font-bold text-green-700">
                ₹
                {(
                  inventoryForm.quantity * inventoryForm.costPerUnit
                ).toFixed(2)}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* FOOTER */}
      <div className="bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 border-t flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 sticky bottom-0 z-10">
        <button
          type="button"
          className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm"
          onClick={() => setShowModal(false)}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm"
        >
          {selectedItem ? 'Update' : 'Add'} Item
        </button>
      </div>

    </form>
  </div>
</div>
      )}

      {/* Add Receipt Modal */}
      {showReceiptModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-4 sm:p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-lg sm:text-xl font-bold">Add Stock Receipt - {selectedItem?.itemName}</h2>
                <button
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors duration-200"
                  onClick={() => setShowReceiptModal(false)}
                >
                  <span className="text-xl sm:text-2xl">×</span>
                </button>
              </div>
            </div>
            <form onSubmit={handleReceiptSubmit}>
              <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">Quantity *</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:border-green-500 focus:outline-none transition-colors duration-200 text-sm"
                      value={receiptForm.quantity}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                          setReceiptForm({ ...receiptForm, quantity: value });
                        }
                      }}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">Cost per Unit (₹) *</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:border-green-500 focus:outline-none transition-colors duration-200 text-sm"
                      value={receiptForm.costPerUnit}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                          setReceiptForm({ ...receiptForm, costPerUnit: value });
                        }
                      }}
                      placeholder="Enter cost per unit"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">Transaction Date *</label>
                    <input
                      type="date"
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:border-green-500 focus:outline-none transition-colors duration-200 text-sm"
                      value={receiptForm.transactionDate}
                      onChange={(e) => setReceiptForm({ ...receiptForm, transactionDate: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">Notes</label>
                  <textarea
                    className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:border-green-500 focus:outline-none transition-colors duration-200 text-sm"
                    value={receiptForm.notes}
                    onChange={(e) => setReceiptForm({ ...receiptForm, notes: e.target.value })}
                    placeholder="Additional notes"
                    rows="3"
                  />
                </div>

                {receiptForm.quantity && receiptForm.costPerUnit && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Total Cost:</span>
                      <span className="text-lg font-bold text-green-700">₹{(receiptForm.quantity * receiptForm.costPerUnit).toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>
              <div className="bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors duration-200 text-sm"
                  onClick={() => setShowReceiptModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors duration-200 text-sm"
                >
                  Add Receipt
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Issue Stock Modal */}
      {showIssueModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-4 sm:p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-lg sm:text-xl font-bold">Issue Stock - {selectedItem?.itemName}</h2>
                <button
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors duration-200"
                  onClick={() => setShowIssueModal(false)}
                >
                  <span className="text-xl sm:text-2xl">×</span>
                </button>
              </div>
            </div>
            <form onSubmit={handleIssueSubmit}>
              <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 sm:p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Available Stock:</span>
                    <span className="text-base sm:text-lg font-bold text-orange-700">{selectedItem?.quantity} {selectedItem?.unit}</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">Quantity to Issue *</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none transition-colors duration-200 text-sm"
                      value={issueForm.quantity}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                          setIssueForm({ ...issueForm, quantity: value });
                        }
                      }}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">Transaction Date *</label>
                    <input
                      type="date"
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none transition-colors duration-200 text-sm"
                      value={issueForm.transactionDate}
                      onChange={(e) => setIssueForm({ ...issueForm, transactionDate: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">Notes</label>
                  <textarea
                    className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none transition-colors duration-200 text-sm"
                    value={issueForm.notes}
                    onChange={(e) => setIssueForm({ ...issueForm, notes: e.target.value })}
                    placeholder="Additional notes"
                    rows="3"
                  />
                </div>
              </div>
              <div className="bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors duration-200 text-sm"
                  onClick={() => setShowIssueModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors duration-200 text-sm"
                >
                  Issue Stock
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Item Transactions Modal */}
      {showTransactionsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 sm:p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-lg sm:text-xl font-bold">Transaction History</h2>
                <button
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors duration-200"
                  onClick={() => setShowTransactionsModal(false)}
                >
                  <span className="text-xl sm:text-2xl">×</span>
                </button>
              </div>
            </div>
            <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-blue-50 to-blue-100 border-b border-gray-200">
                    <tr>
                      <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Date</th>
                      <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Type</th>
                      <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Quantity</th>
                      <th className="hidden sm:table-cell px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Cost/Unit</th>
                      <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Total Cost</th>
                      <th className="hidden md:table-cell px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {currentItemTransactions.map((txn) => (
                      <tr key={txn._id} className="hover:bg-gray-50 transition-colors duration-150">
                        <td className="px-3 sm:px-6 py-3 sm:py-4 text-sm text-gray-700">{new Date(txn.transactionDate).toLocaleDateString()}</td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4">
                          {txn.transactionType === 'receipt' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                              <FiTrendingUp size={12} />
                              <span className="hidden sm:inline">Receipt</span>
                              <span className="sm:hidden">+</span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium">
                              <FiTrendingDown size={12} />
                              <span className="hidden sm:inline">Issue</span>
                              <span className="sm:hidden">-</span>
                            </span>
                          )}
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 text-sm text-gray-700">{txn.quantity} {txn.unit}</td>
                        <td className="hidden sm:table-cell px-6 py-4">
                          {txn.priceChange !== 0 ? (
                            <div className="flex items-center gap-2">
                              <span
                                style={{
                                  color: txn.priceChange > 0 ? '#ef4444' : '#22c55e',
                                  fontWeight: '600'
                                }}
                              >
                                ₹{txn.costPerUnit}
                              </span>
                              {txn.priceChange > 0 ? (
                                <span style={{ color: '#ef4444', fontSize: '12px' }}>
                                  ↑ +₹{Math.abs(txn.priceChange)}
                                </span>
                              ) : (
                                <span style={{ color: '#22c55e', fontSize: '12px' }}>
                                  ↓ -₹{Math.abs(txn.priceChange)}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span style={{ color: '#666' }}>₹{txn.costPerUnit}</span>
                          )}
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4">
                          <div className="text-sm font-bold text-gray-900">₹{txn.totalCost?.toFixed(2)}</div>
                          {/* Mobile-only additional info */}
                          <div className="sm:hidden mt-1 space-y-1">
                            <div className="text-xs text-gray-600">
                              Cost/Unit: ₹{txn.costPerUnit}
                            </div>
                            {txn.notes && (
                              <div className="text-xs text-gray-600 truncate max-w-[150px]">{txn.notes}</div>
                            )}
                          </div>
                        </td>
                        <td className="hidden md:table-cell px-6 py-4 text-sm text-gray-700">{txn.notes || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination for Item Transactions */}
              {itemTransactions.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4 mt-6">
                  <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <span>Show</span>
                      <select
                        className="px-3 py-1 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200"
                        value={itemTransactionsPerPage}
                        onChange={(e) => {
                          setItemTransactionsPerPage(Number(e.target.value));
                          setItemTransactionPage(1);
                        }}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <span>entries</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        className="px-3 py-1 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400 text-gray-700 rounded-lg font-medium transition-colors duration-200"
                        onClick={() => paginateItemTransactions(itemTransactionPage - 1)}
                        disabled={itemTransactionPage === 1}
                      >
                        Previous
                      </button>
                      <span className="px-3 py-1 text-sm text-gray-600">Page {itemTransactionPage} of {totalItemTransactionPages}</span>
                      <button
                        className="px-3 py-1 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400 text-gray-700 rounded-lg font-medium transition-colors duration-200"
                        onClick={() => paginateItemTransactions(itemTransactionPage + 1)}
                        disabled={itemTransactionPage === totalItemTransactionPages}
                      >
                        Next
                      </button>
                    </div>
                    <div className="text-sm text-gray-600">
                      <span>Total: {itemTransactions.length} transactions (Last 7 days)</span>
                    </div>
                  </div>
                </div>
              )}

              {itemTransactions.length === 0 && (
                <div className="bg-gray-50 rounded-lg p-12 text-center">
                  <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FiCalendar className="text-gray-400 text-3xl" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Transactions Found</h3>
                  <p className="text-gray-600">No transactions found for this item in the last 7 days.</p>
                </div>
              )}
            </div>
            <div className="bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 flex justify-end">
              <button
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors duration-200 text-sm"
                onClick={() => setShowTransactionsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockManagement;
